import {i18n} from '@lingui/core';
import {t} from '@lingui/macro';

import {isRegexPart, RegexCharacterClassesEnum, RegexPart} from '../commons/stringTemplateModel';
import {buildExpressionName, Expression, isPreviousResultRef} from './expressionModel';
import {ExpressionRef} from './expressionRefModel';
import {InferredType, isListType, ResultTypesEnum} from './infer/expressionResultModel';
import {TransformerIdsEnum} from './infer/transformers';

export const previousResultLabel = i18n._(t('expression-previous-result')`Previous result`);

const propertyLabels: {[id: string]: string} = {
  length: i18n._(t('expression-prop-length')`Length`),
  domain: i18n._(t('expression-prop-domain')`Domain`),
  local_part: i18n._(t('expression-prop-localpart')`Local part`),
  protocol: i18n._(t('expression-prop-protocol')`Protocol`),
  hostname: i18n._(t('expression-prop-hostname')`Host name`),
  path: i18n._(t('expression-prop-path')`Path`),
  query_string: i18n._(t('expression-prop-querystring')`Query string`),
  date: i18n._(t('expression-prop-date')`Date`),
  day_of_month: i18n._(t('expression-prop-dateofmonth')`Day of month`),
  id: i18n._(t('expression-prop-id')`ID`),
  id_conversation: i18n._(t('expression-prop-idconversation')`Conversation ID`),
  id_message: i18n._(t('expression-prop-idmessage')`Message ID`),
  id_comment: i18n._(t('expression-prop-idcomment')`Comment ID`),
  id_tag: i18n._(t('expression-prop-idtag')`Tag ID`),
  id_topic: i18n._(t('expression-prop-idtopic')`Link ID`),
  id_contact: i18n._(t('expression-prop-idcontact')`Contact ID`),
  id_account: i18n._(t('expression-prop-idaccount')`Account ID`),
  id_inbox: i18n._(t('expression-prop-idinbox')`Inbox ID`),
  id_teammate: i18n._(t('expression-prop-idteammate')`Teammate ID`),
  description: i18n._(t('expression-prop-description')`Description`),
  domains: i18n._(t('expression-prop-domains')`Domains`),
  month: i18n._(t('expression-prop-month')`Month`),
  year: i18n._(t('expression-prop-year')`Year`),
  hours: i18n._(t('expression-prop-hours')`Hours`),
  minutes: i18n._(t('expression-prop-minutes')`Minutes`),
  seconds: i18n._(t('expression-prop-seconds')`Seconds`),
  full_name: i18n._(t('expression-prop-fullname')`Full name`),
  first_name: i18n._(t('expression-prop-firstname')`First name`),
  last_name: i18n._(t('expression-prop-lastname')`Last name`),
  username: i18n._(t('expression-prop-username')`Username`),
  email: i18n._(t('expression-prop-email')`Email`),
  phone: i18n._(t('expression-prop-phone')`Phone number`),
  twitter: i18n._(t('expression-prop-twitter')`Twitter handle`),
  handle: i18n._(t('expression-prop-handle')`Handle`),
  is_available: i18n._(t('expression-prop-isavailable')`Is available`),
  is_global_admin: i18n._(t('expression-prop-isglobaladmin')`Is company administrator`),
  is_visitor: i18n._(t('expression-prop-isvisitor')`Is guest`),
  is_empty: i18n._(t('expression-prop-isempty')`Is empty`),
  is_not_empty: i18n._(t('expression-prop-isnotempty')`Is not empty`),
  is_false: i18n._(t('expression-prop-isfalse')`Is false`),
  name: i18n._(t('expression-prop-name')`Name`),
  subject: i18n._(t('expression-prop-subject')`Subject`),
  language: i18n._(t('expression-prop-language')`Language`),
  assignee: i18n._(t('expression-prop-assignee')`Assignee`),
  url: i18n._(t('expression-prop-url')`URL`),
  num_messages: i18n._(t('expression-prop-nummessages')`Number of messages`),
  inbox_owner: i18n._(t('expression-prop-inboxowner')`Inbox owner`),
  contact: i18n._(t('expression-prop-contact')`Contact`),
  account: i18n._(t('expression-prop-account')`Account`),
  tags: i18n._(t('expression-prop-tags')`Tags`),
  topics: i18n._(t('expression-prop-topics')`Links`),
  inboxes: i18n._(t('expression-prop-inboxes')`Inboxes`),
  participants: i18n._(t('expression-prop-participants')`Participants`),
  sender: i18n._(t('expression-prop-sender')`Sender`),
  author: i18n._(t('expression-prop-author')`Author`),
  body: i18n._(t('expression-prop-body')`Body`),
  mentions: i18n._(t('expression-prop-mentions')`Mentions`),
  conversation: i18n._(t('expression-prop-conversation')`Conversation`),
  message: i18n._(t('expression-prop-message')`Message`),
  comment: i18n._(t('expression-prop-comment')`Comment`),
  activity: i18n._(t('expression-prop-event')`Activity`),
  reference: i18n._(t('expression-prop-reference')`Reference`),
  status: i18n._(t('expression-prop-status')`Status`),
  owner: i18n._(t('expression-prop-owner')`Owner`),
  teammates: i18n._(t('expression-prop-teammates')`Teammates`),
  added_tag: i18n._(t('expression-prop-addedtag')`Added tag`),
  removed_tag: i18n._(t('expression-prop-removedtag')`Removed tag`),
  added_topic: i18n._(t('expression-prop-addedtopic')`Added link`),
  removed_topic: i18n._(t('expression-prop-removedtopic')`Removed link`),
  previous_assignee: i18n._(t('expression-prop-previousassignee')`Previous assignee`),
};

const propertyTooltips: {[id: string]: string} = {
  id_conversation: i18n._(t('expression-prop-tooltip-idconversation')`Example: "cnv_123"`),
  id_message: i18n._(t('expression-prop-tooltip-idmessage')`Example: "msg_123"`),
  id_comment: i18n._(t('expression-prop-tooltip-idcomment')`Example: "com_123"`),
  id_tag: i18n._(t('expression-prop-tooltip-idtag')`Example: "tag_123"`),
  id_topic: i18n._(t('expression-prop-tooltip-idtopic')`Example: "top_123"`),
  id_contact: i18n._(t('expression-prop-tooltip-idcontact')`Example: "crd_123"`),
  id_account: i18n._(t('expression-prop-tooltip-idaccount')`Example: "act_123"`),
  id_inbox: i18n._(t('expression-prop-tooltip-idinbox')`Example: "inb_123"`),
  id_teammate: i18n._(t('expression-prop-tooltip-idteammate')`Example: "tea_123"`),
  length_string: i18n._(
    t('expression-prop-tooltip-lengthstring')`Number of characters, in UTF-16 code units.`,
  ),
  length_list: i18n._(t('expression-prop-tooltip-lengthlist')`Number of elements in the list.`),
  protocol: i18n._(t('expression-prop-tooltip-protocol')`Protocol of the URL. Example: "https:", "http:".`),
  num_messages: i18n._(
    t('expression-prop-tooltip-nummessages')`Total number of messages in the conversation.`,
  ),
  inbox_owner: i18n._(
    t(
      'expression-prop-tooltip-inboxowner',
    )`Teammate to whom an individual inbox belongs. Undefined for team inboxes.`,
  ),
  assignee: i18n._(
    t(
      'expression-prop-tooltip-assignee',
    )`Assignee of the conversation. Undefined if the conversation is unassigned.`,
  ),
  participants: i18n._(
    t(
      'expression-prop-tooltip-participants',
    )`Teammates who comment, @mention a teammate, reply directly, or subscribe to the conversation.`,
  ),
  sender: i18n._(t('expression-prop-tooltip-sender')`Contact who sent the message.`),
  author_message: i18n._(
    t(
      'expression-prop-tooltip-authormessage',
    )`Teammate who sent the message. Undefined if the message was not sent by a teammate.`,
  ),
  author_comment: i18n._(t('expression-prop-tooltip-authorcomment')`Teammate who sent the comment.`),
  mentions: i18n._(t('expression-prop-tooltip-mentions')`All teammates @mentioned in the comment.`),
  is_empty: i18n._(t('expression-prop-tooltip-isempty')`List contains no elements.`),
  is_not_empty: i18n._(t('expression-prop-tooltip-isnotempty')`List contains at least one element.`),
  email_teammate: i18n._(t('expression-prop-tooltip-emailteammate')`Email used to sign in to Front.`),
  is_available: i18n._(t('expression-prop-tooltip-isavailable')`False if teammate is set as Out of Office.`),
  is_visitor: i18n._(
    t(
      'expression-prop-tooltip-isvisitor',
    )`True if teammate is a person outside of your Front account who was invited to the conversation as a Guest.`,
  ),
  is_false: i18n._(t('expression-prop-tooltip-isfalse')`True if the boolean is false.`),
  contact_conversation: i18n._(
    t('expression-prop-tooltip-contact')`Primary recipient of the last message of the conversation.`,
  ),
  account_conversation: i18n._(
    t(
      'expression-prop-tooltip-account',
    )`Account of the primary recipient of the last message of the conversation.`,
  ),
  previous_assignee: i18n._(
    t(
      'expression-prop-tooltip-previousassignee',
    )`For triggers that cause the assignee to change, return the assignee that existed immediately before the event happened.`,
  ),
};

export interface AtomDescription {
  name: string;
  tooltip?: string;
}

export function getPropertyLabel(propertyId: string) {
  return propertyLabels[propertyId];
}

export function getPropertyDescription(propertyId: string, type: ResultTypesEnum): AtomDescription {
  // Types can override the name.
  const typeKey = `${propertyId}_${type}`;

  return {
    name: propertyLabels[typeKey] || propertyLabels[propertyId] || propertyId,
    tooltip: propertyTooltips[typeKey] || propertyTooltips[propertyId],
  };
}

const methodLabels: {[id: string]: string} = {
  is_true: i18n._(t('expression-method-istrue')`Is true`),
  is_false: i18n._(t('expression-method-isfalse')`Is false`),
  is_undefined: i18n._(t('expression-method-isundefined')`Does not exist`),
  is_defined: i18n._(t('expression-method-isdefined')`Exists`),
  add: i18n._(t('expression-method-add')`Add`),
  subtract: i18n._(t('expression-method-subtract')`Subtract`),
  start_of: i18n._(t('expression-method-startof')`Set to start of period`),
  end_of: i18n._(t('expression-method-endof')`Set to end of period`),
  capitalize: i18n._(t('expression-method-capitalize')`Capitalize`),
  to_uppercase: i18n._(t('expression-method-touppercase')`Convert to upper case`),
  to_lowercase: i18n._(t('expression-method-tolowercase')`Convert to lower case`),
  substring: i18n._(t('expression-method-substring')`Extract between indexes`),
  extract_text: i18n._(t('expression-method-extracttext')`Extract text`),
  split: i18n._(t('expression-method-split')`Split by`),
  join: i18n._(t('expression-method-join')`Join`),
  to_number: i18n._(t('expression-method-tonumber')`Parse as number`),
  to_email: i18n._(t('expression-method-toemail')`Parse as Email`),
  to_url: i18n._(t('expression-method-tourl')`Parse as URL`),
  to_json: i18n._(t('expression-method-tojson')`Parse as JSON`),
  to_tag: i18n._(t('expression-method-totag')`Convert to Tag`),
  [TransformerIdsEnum.TO_INBOX]: i18n._(t('expression-method-toinbox')`Convert to Inbox`),
  to_teammate: i18n._(t('expression-method-toteammate')`Convert to Teammate`),
  to_contact: i18n._(t('expression-method-tocontact')`Convert to Contact`),
  to_iso8601: i18n._(t('expression-method-toiso8601')`Format as ISO 8601`),
  to_unix_timestamp: i18n._(t('expression-method-tounix')`Format as Unix timestamp`),
  format: i18n._(t('expression-method-format')`Format`),
  domain_to_account: i18n._(t('expression-method-domaintoaccount')`Get Account from domain`),
  extract_urls: i18n._(t('expression-method-extracturls')`Extract URLs`),
  extract_emails: i18n._(t('expression-method-extractemails')`Extract email addresses`),
  get_query_param: i18n._(t('expression-method-getqueryparam')`Query parameter`),
  get_recipients: i18n._(t('expression-method-getrecipients')`Get recipients`),
  get_message_body: i18n._(t('expression-method-getmessagebody')`Body`),
  get_header: i18n._(t('expression-method-getheader')`Get header`),
  detect_language: i18n._(t('expression-method-detectlanguage')`Detect language`),
  first: i18n._(t('expression-method-first')`First element`),
  last: i18n._(t('expression-method-last')`Last element`),
  nth: i18n._(t('expression-method-nth')`Nth element`),
  beginning: i18n._(t('expression-method-beginning')`First n elements`),
  end: i18n._(t('expression-method-end')`Last n elements`),
  filter: i18n._(t('expression-method-filter')`Filter`),
  map: i18n._(t('expression-method-map')`Transform each element (map)`),
  find: i18n._(t('expression-method-find')`Find`),
  sort_by: i18n._(t('expression-method-sortby')`Sort by`),
  dedup: i18n._(t('expression-method-dedup')`Deduplicate elements`),
  create_string: i18n._(t('expression-method-createstring')`Create text`),
  create_url: i18n._(t('expression-method-createurl')`Create URL`),
  create_teammate_list: i18n._(t('expression-method-createteammatelist')`Create teammate list`),
  use_previous: i18n._(t('expression-method-useprevious')`Use result of variable`),
  get_json_property: i18n._(t('expression-method-getjsonproperty')`Extract object property`),
  as_array: i18n._(t('expression-method-expectarray')`Parse as Array`),
  as_string: i18n._(t('expression-method-expectstring')`Parse as String`),
  as_number: i18n._(t('expression-method-expectnumber')`Parse as Number`),
  as_boolean: i18n._(t('expression-method-expectboolean')`Parse as Boolean`),
  fetch: i18n._(t('expression-method-fetch')`Fetch from server`),
  parse: i18n._(t('expression-method-parse')`Parse response`),
  get_circle: i18n._(t('expression-method-get-circle')`Get teammate group`),
  [TransformerIdsEnum.GET_COLUMN_VALUE]: i18n._(t('expression-method-get-column-value')`Get column value`),
};

const methodTooltips: {[id: string]: string} = {
  capitalize: i18n._(t('expression-method-tooltip-capitalize')`Capitalize first word of each sentence.`),
  split: i18n._(
    t(
      'expression-method-tooltip-split',
    )`Divide text into a list. The division is done by searching for a separator pattern.`,
  ),
  join: i18n._(
    t(
      'expression-method-tooltip-join',
    )`Return a new text by concanetating all of the elements of the list, separated by commas or the specified separator text.`,
  ),
  to_number: i18n._(
    t('expression-method-tooltip-tonumber')`Convert from type text to type number. Undefined if invalid.`,
  ),
  to_url: i18n._(
    t('expression-method-tooltip-tourl')`Convert from type text to type URL. Undefined if invalid.`,
  ),
  to_json: i18n._(t('expression-method-tooltip-tojson')`Undefined is text is not valid JSON data.`),
  to_tag: i18n._(
    t(
      'expression-method-tooltip-totag',
    )`Retrieve a tag by name: private tag if in a personal rule or a team tag otherwise.`,
  ),
  [TransformerIdsEnum.TO_INBOX]: i18n._(
    t(
      'expression-method-tooltip-toinbox',
    )`Retrieve an inbox by name: private inbox if in a personal rule or a team inbox otherwise.`,
  ),
  to_email: i18n._(
    t('expression-method-tooltip-toemail')`Convert from type text to type email. Undefined if invalid.`,
  ),
  to_teammate: i18n._(
    t(
      'expression-method-tooltip-toteammate',
    )`Fetch a teammate by its email address. Undefined if no teammate is associated.`,
  ),
  to_contact: i18n._(
    t(
      'expression-method-tooltip-tocontact',
    )`Fetch a contact by its email address. Undefined if no contact is associated.`,
  ),
  get_recipients: i18n._(
    t('expression-method-tooltip-getrecipients')`List recipients of the message in the selected fields.`,
  ),
  get_header: i18n._(
    t('expression-method-tooltip-getheader')`Select the value of the specified header name.`,
  ),
  detect_language: i18n._(
    t(
      'expression-method-tooltip-detectlanguage',
    )`Example: "english", "french", "german", "italian", "spanish", "portugese"…`,
  ),
  filter: i18n._(
    t('expression-method-tooltip-filter')`Restrict list to elements matching your filter conditions.`,
  ),
  map: i18n._(
    t(
      'expression-method-tooltip-map',
    )`Create a new list populated with the results of applying the provided transformation on every element.`,
  ),
  find: i18n._(
    t('expression-method-tooltip-find')`Return the first element where the provided transformation is true.`,
  ),
  sort_by: i18n._(
    t('expression-method-tooltip-sortby')`Sort elements according to the provided transformation.`,
  ),
  substring: i18n._(
    t(
      'expression-method-tooltip-substring',
    )`Return part of the text between the start and end indexes. Indexes start at 0.`,
  ),
  extract_text: i18n._(
    t(
      'expression-method-tooltip-extracttext',
    )`Search for patterns matching the expression you provide. You can mix text and regular expressions.`,
  ),
  nth: i18n._(t('expression-method-tooltip-nth')`Pick element at provided index. Indexes start at 0.`),
  to_iso8601: i18n._(
    t(
      'expression-method-tooltip-toiso8601',
    )`Convert date to the ISO 8601 standard. Example: "1988-04-25T03:17:09Z".`,
  ),
  to_unix_timestamp: i18n._(
    t(
      'expression-method-tooltip-tounix',
    )`Return the number of seconds since the Unix Epoch (Jan 1 1970 12AM UTC). Example: 577937829.`,
  ),
  format: i18n._(
    t(
      'expression-method-tooltip-format',
    )`Convert the date to a human-readable text. Example: "April 25, 1988 3:17 AM".`,
  ),
  use_previous: i18n._(
    t(
      'expression-method-tooltip-useprevious',
    )`Use the value of a previously defined variable as a starting point.`,
  ),
  get_json_property: i18n._(
    t(
      'expression-method-tooltip-getjsonproperty',
    )`Extract a field from a JSON object. You can only access top-level fields.`,
  ),
  as_array: i18n._(t('expression-method-tooltip-asarray')`Interpret the JSON data as an array.`),
  as_string: i18n._(t('expression-method-tooltip-asstring')`Interpret the JSON data as a text.`),
  as_number: i18n._(t('expression-method-tooltip-asnumber')`Interpret the JSON data as a number.`),
  as_boolean: i18n._(t('expression-method-tooltip-asboolean')`Interpret the JSON data as a boolean.`),
  is_undefined: i18n._(t('expression-prop-tooltip-isundefined')`True if the previous field does not exist.`),
  is_defined: i18n._(t('expression-prop-tooltip-isdefined')`True if the previous field exists.`),
};

export function getMethodLabel(transform: string): AtomDescription {
  return {
    name: methodLabels[transform] || transform,
    tooltip: methodTooltips[transform],
  };
}

const operationLabels: {[id: string]: string} = {
  equals: i18n._(t('expression-op-equals')`Is equal to`),
  not_equal: i18n._(t('expression-op-notequal')`Is not equal to`),
  greater_than: i18n._(t('expression-op-greaterthan')`Is greater than`),
  lower_than: i18n._(t('expression-op-lowerthan')`Is lower than`),
  starts_with: i18n._(t('expression-op-startswith')`Starts with`),
  ends_with: i18n._(t('expression-op-endswith')`Ends with`),
  contains: i18n._(t('expression-op-contains')`Contains`),
  not_contain: i18n._(t('expression-op-notcontain')`Does not contain`),
};

const enumLabels: {[key: string]: string} = {
  ...operationLabels,
  from: i18n._(t('expression-enum-from')`"From"`),
  to: i18n._(t('expression-enum-to')`"To"`),
  cc: i18n._(t('expression-enum-cc')`"Cc"`),
  bcc: i18n._(t('expression-enum-bcc')`"Bcc"`),
  to_cc: i18n._(t('expression-enum-tocc')`"To" or "Cc"`),
  all_recipients: i18n._(t('expression-enum-allrecipients')`All recipients`),
  first: i18n._(t('expression-enum-first')`First occurrence`),
  all: i18n._(t('expression-enum-all')`All occurrences`),
  ascending: i18n._(t('expression-enum-ascending')`Ascending`),
  descending: i18n._(t('expression-enum-descending')`Descending`),
  line: i18n._(t('expression-enum-line')`Line break`),
  comma: i18n._(t('expression-enum-comma')`Comma`),
  semicolon: i18n._(t('expression-enum-semicolon')`Semicolon`),
  whitespace: i18n._(t('expression-enum-whitespace')`Whitespace`),
  empty: i18n._(t('expression-enum-empty')`Empty text`),
  space: i18n._(t('expression-enum-space')`Space`),
  custom_splitter: i18n._(t('expression-enum-customsplitter')`Custom separator`),
  custom_join: i18n._(t('expression-enum-customjoin')`Custom separator`),
  date_time: i18n._(t('expression-enum-datetime')`Date and time`),
  date: i18n._(t('expression-enum-date')`Date`),
  time: i18n._(t('expression-enum-time')`Time`),
  second: i18n._(t('expression-enum-second')`Second`),
  minute: i18n._(t('expression-enum-minute')`Minute`),
  hour: i18n._(t('expression-enum-hour')`Hour`),
  day: i18n._(t('expression-enum-day')`Day`),
  week: i18n._(t('expression-enum-week')`Week`),
  month: i18n._(t('expression-enum-month')`Month`),
  quarter: i18n._(t('expression-enum-quarter')`Quarter`),
  year: i18n._(t('expression-enum-year')`Year`),
  monday: i18n._(t('expression-enum-monday')`Monday`),
  sunday: i18n._(t('expression-enum-sunday')`Sunday`),
  object: i18n._(t('expression-enum-object')`Object`),
  array: i18n._(t('expression-enum-array')`Array`),
  string: i18n._(t('expression-enum-string')`Text`),
  number: i18n._(t('expression-enum-number')`Number`),
  boolean: i18n._(t('expression-enum-boolean')`Boolean`),
  json: i18n._(t('expression-enum-json')`JSON`),
  none: i18n._(t('expression-enum-none')`None`),
  bearer: i18n._(t('expression-enum-bearer')`Bearer token`),
  basic: i18n._(t('expression-enum-basic')`Basic`),
  oauth2: i18n._(t('expression-enum-oauth2')`OAuth 2`),
  sign_in: i18n._(t('expression-enum-signin')`Sign in`),
  form_urlencoded: i18n._(t('expression-enum-formdata')`Form (URL encoded)`),
};

const enumTooltips: {[key: string]: string} = {
  greater_than: i18n._(t('expression-op-tooltip-greaterthan')`Strictly greater than value.`),
  lower_than: i18n._(t('expression-op-toolip-lowerthan')`Strictly lower than value.`),
  sign_in: i18n._(
    t('expression-op-toolip-signin')`Authenticate using a cookie session set by a sign-in request.`,
  ),
};

export function getEnumName(value: string): string {
  return enumLabels[value] || value;
}

export function getEnumTooltip(value: string): string {
  return enumTooltips[value] || '';
}

export function getEnumLabel(value: string): AtomDescription {
  return {name: getEnumName(value), tooltip: getEnumTooltip(value)};
}

const parameterLabels: {[key: string]: string} = {
  include_quote: i18n._(t('expression-param-includequote')`Include past email history`),
  multiline: i18n._(t('expression-param-multiline')`Match multiple lines`),
  abbreviated: i18n._(t('expression-param-abbreviated')`Abbreviated format`),
  custom_splitter: i18n._(t('expression-param-customsplitter')`Separator`),
  custom_join: i18n._(t('expression-param-customjoin')`Separator`),
  pattern: i18n._(t('expression-param-pattern')`Enter text or select placeholder`),
  before: '',
  after: '',
  value: i18n._(t('expression-param-value')`Value`),
  amount: i18n._(t('expression-param-amount')`Amount`),
  locale: i18n._(t('expression-param-locale')`en-US`),
  endpoint: i18n._(t('expression-param-endpoint')`HTTPS endpoint`),
  force_create_tag: i18n._(t('expression-param-forcecreatetag')`Create tag if it does not exist`),
  is_visible_in_p2: i18n._(t('expression-param-isvisibleinp2')`Display in conversations list`),
};

export function getParameterLabel(value: string): string {
  return parameterLabels[value] ?? '';
}

const expressionTypeLabels: Record<ResultTypesEnum, string> = {
  string: i18n._(t('expression-type-string')`Text`),
  number: i18n._(t('expression-type-number')`Number`),
  boolean: i18n._(t('expression-type-boolean')`Boolean`),
  email: i18n._(t('expression-type-email')`Email`),
  url: i18n._(t('expression-type-url')`URL`),
  datetime: i18n._(t('expression-type-date')`Date`),
  list: i18n._(t('expression-type-list')`List`),
  contact: i18n._(t('expression-type-contact')`Contact`),
  account: i18n._(t('expression-type-account')`Account`),
  teammate: i18n._(t('expression-type-teammate')`Teammate`),
  inbox: i18n._(t('expression-type-inbox')`Inbox`),
  tag: i18n._(t('expression-type-tag')`Tag`),
  topic: i18n._(t('expression-type-topic')`Link`),
  conversation: i18n._(t('expression-type-conversation')`Conversation`),
  message: i18n._(t('expression-type-message')`Message`),
  comment: i18n._(t('expression-type-comment')`Comment`),
  circle: i18n._(t('expression-type-circle')`Teammate group`),
  json: i18n._(t('expression-type-json')`JSON`),
  activity: i18n._(t('expression-type-activity')`Event`),
  autolinker: i18n._(t('expression-type-autolinker')`Autolinker`),
  previewer: i18n._(t('expression-type-previewer')`Previewer`),
  undefined: i18n._(t('expression-type-undefined')`Undefined`),
  unknown: i18n._(t('expression-type-unknown')`Unknown`),
  data_table: i18n._(t('expression-type-data-fields-table')`Data table`),
};

export function getLabelForInferredType(inferredType: InferredType): string {
  if (isListType(inferredType)) {
    return `${getLabelForInferredType(inferredType.itemType)} list`;
  }

  return expressionTypeLabels[inferredType.type] || inferredType.type;
}

const regexPartLabels: Record<RegexCharacterClassesEnum, string> = {
  digit: i18n._(t('expression-regex-digit')`Digits`),
  alpha: i18n._(t('expression-regex-letter')`Letters`),
  alpha_digit: i18n._(t('expression-regex-alphadigit')`Letters or digits`),
  word: i18n._(t('expression-regex-word')`Word characters`),
  printable: i18n._(t('expression-regex-printable')`Printable characters`),
  word_boundary: i18n._(t('expression-regex-wordboundary')`Word boundary`),
  any: i18n._(t('expression-regex-any')`Any characters`),
};

export function getLabelForRegexCharClass(charClass: RegexCharacterClassesEnum): string {
  return regexPartLabels[charClass] ?? expressionTypeLabels.unknown;
}

export function getLabelForRegex(part: RegexPart): string {
  return `${getLabelForRegexCharClass(part.char)}${getLabelForRegexRepeat(part)}`;
}

function getLabelForRegexRepeat(part: RegexPart): string {
  return part.repeat ? ` {${part.repeat}}` : '';
}

const regexPartSources: Record<RegexCharacterClassesEnum, string> = {
  digit: '[0-9]',
  alpha: '[a-z]',
  alpha_digit: '[a-z0-9]',
  word: '\\w',
  word_boundary: '\\b',
  printable: '\\S',
  any: '.',
};

export function getSourceForRegexCharClass(charClass: RegexCharacterClassesEnum): string {
  return regexPartSources[charClass] || '';
}

export function getLabelForTemplatePart(
  part: ExpressionRef | RegexPart,
  expressions: ReadonlyArray<Expression>,
): string | undefined {
  if (isRegexPart(part)) {
    return getLabelForRegex(part);
  }

  if (isPreviousResultRef(part)) {
    return previousResultLabel;
  }

  const staticLabel = getPropertyLabel(part.expression);
  if (staticLabel) {
    return staticLabel;
  }

  const expression = expressions.find((e) => e.id === part.expression);
  return expression ? buildExpressionName(expression) : undefined;
}

export const referenceNotFoundError = i18n._(t('expression-error-not-found')`Referenced variable not found.`);

export const variableMissingError = i18n._(t('expression-error-missing')`Missing variable`);

export function getTypeMismatchError(expectedType: InferredType, receivedType: InferredType) {
  const expectedTypeLabel = getLabelForInferredType(expectedType);
  const receivedTypeLabel = getLabelForInferredType(receivedType);
  return i18n._(
    t(
      'expression-error-type-mismatch',
    )`Expecting a ${expectedTypeLabel}, but receiving a ${receivedTypeLabel}.`,
  );
}
